import { useState } from "react";
import { ArrowRight, ArrowUpRight, EnvelopeSimple, InstagramLogo, MapPin, CheckCircle } from "@phosphor-icons/react";

const INTAKE_URL = "/intake";
const EVERFIT_URL = "https://invite.everfit.io/Q9TTSYVZYA";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const onSubmit = (e) => {
    e.preventDefault();
    // Quick contact form — not wired to backend yet.
    // For now, this just confirms receipt on the client.
    setSent(true);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-6 md:px-12 lg:px-20 py-16 lg:py-20 border-b border-white/10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 max-w-7xl mx-auto">
        {/* Left - copy + meta */}
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#FF1B91]" />
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Contact / 08</p>
          </div>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.88] tracking-tighter mb-8">
            Tell me what you&apos;re{" "}
            <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">chasing.</span>
          </h2>
          <p className="text-white/75 leading-relaxed max-w-md mb-10">
            Two ways to start: send a quick message if you just want to chat, or jump straight into the full intake if you&apos;re ready to roll.
          </p>

          <div className="space-y-5">
            <a href="mailto:hello@sweatwithslay.com" data-testid="contact-email" className="flex items-start gap-4 group">
              <div className="w-10 h-10 border border-white/15 flex items-center justify-center group-hover:border-[#FF1B91] group-hover:text-[#FF1B91] transition-colors">
                <EnvelopeSimple size={18} weight="bold" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Email</div>
                <div className="text-white font-medium mt-1 group-hover:text-[#FF1B91] transition-colors">hello@sweatwithslay.com</div>
              </div>
            </a>
            <a href="https://instagram.com/sweatwithslay" target="_blank" rel="noreferrer noopener" data-testid="contact-instagram" className="flex items-start gap-4 group">
              <div className="w-10 h-10 border border-white/15 flex items-center justify-center group-hover:border-[#FF1B91] group-hover:text-[#FF1B91] transition-colors">
                <InstagramLogo size={18} weight="bold" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">DM the brand</div>
                <div className="text-white font-medium mt-1 group-hover:text-[#FF1B91] transition-colors">@sweatwithslay</div>
              </div>
            </a>
            <a href="https://instagram.com/alannajohnson" target="_blank" rel="noreferrer noopener" data-testid="contact-instagram-personal" className="flex items-start gap-4 group">
              <div className="w-10 h-10 border border-white/15 flex items-center justify-center group-hover:border-[#FF1B91] group-hover:text-[#FF1B91] transition-colors">
                <InstagramLogo size={18} weight="bold" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">DM AJ</div>
                <div className="text-white font-medium mt-1 group-hover:text-[#FF1B91] transition-colors">@alannajohnson</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-white/15 flex items-center justify-center">
                <MapPin size={18} weight="bold" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Based</div>
                <div className="text-white font-medium mt-1">Online · Coaching anywhere in the world</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right - two cards */}
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Quick contact form */}
          <div className="relative border border-white/10 p-7 lg:p-8 bg-white/[0.02]">
            <span className="absolute -top-3 left-6 px-3 bg-[#0A0A0A] text-[10px] uppercase tracking-[0.3em] text-white/70 font-semibold">
              Quick hello
            </span>
            {sent ? (
              <div data-testid="contact-quick-success" className="py-10 text-center">
                <CheckCircle size={48} weight="fill" className="text-[#FF1B91] mb-4 mx-auto" />
                <h3 className="font-display font-black uppercase text-2xl tracking-tighter mb-2">Got it.</h3>
                <p className="text-white/70 text-sm">I&apos;ll reply within 48 hours. If you want to fast-track, the full intake is right next to this →</p>
                <button
                  data-testid="contact-quick-reset"
                  onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }}
                  className="mt-6 text-[10px] uppercase tracking-[0.3em] font-bold text-white/50 hover:text-white"
                >
                  Send another →
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-display font-black uppercase text-2xl lg:text-3xl tracking-tighter mb-2">Just a quick note?</h3>
                <p className="text-sm text-white/70 mb-6">Not ready for the full intake? Send a quick message and I&apos;ll get back to you.</p>
                <form onSubmit={onSubmit} data-testid="contact-quick-form" className="space-y-5">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-semibold">Name</span>
                    <input
                      data-testid="quick-input-name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="input-brutal mt-1"
                      placeholder="Your name"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-semibold">Email</span>
                    <input
                      data-testid="quick-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input-brutal mt-1"
                      placeholder="you@email.com"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-semibold">Message</span>
                    <textarea
                      data-testid="quick-input-message"
                      required
                      rows={3}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="input-brutal mt-1 resize-none"
                      placeholder="What's up?"
                    />
                  </label>
                  <button
                    type="submit"
                    data-testid="quick-submit"
                    className="w-full inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-[#FF1B91] hover:text-white px-6 py-3.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                  >
                    Send message <ArrowRight size={14} weight="bold" />
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Full intake card */}
          <div className="relative border border-[#FF1B91] p-7 lg:p-8 bg-[#FF1B91]/[0.04] overflow-hidden flex flex-col">
            <span className="absolute -top-3 left-6 px-3 bg-[#0A0A0A] text-[10px] uppercase tracking-[0.3em] text-[#FF1B91] font-semibold">
              Full intake · Recommended
            </span>
            <span className="absolute -right-20 -bottom-20 w-64 h-64 rounded-full bg-[#FF1B91]/10 blur-3xl pointer-events-none" />

            <div className="relative flex flex-col flex-1">
              <p className="font-serif-italic text-xl text-white/95 leading-snug mb-4">
                &ldquo;Slay is not a finish line. It&apos;s a mindset.&rdquo;
              </p>
              <h3 className="font-display font-black uppercase text-2xl lg:text-3xl tracking-tighter mb-3 leading-[0.95]">
                Ready to commit?{" "}
                <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">start here.</span>
              </h3>
              <p className="text-sm text-white/75 leading-relaxed mb-6 flex-1">
                The full Sweat With Slay intake takes about 15 minutes. Covers your goals, training history, schedule, equipment, and lifestyle so I can build your plan and recommend the right tier.
              </p>

              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { k: "<5", v: "Minutes" },
                  { k: "48h", v: "Reply" },
                  { k: "4", v: "Plan options" },
                ].map((s) => (
                  <div key={s.v} className="border border-white/10 p-3 text-center">
                    <div className="font-display font-black text-xl text-white">{s.k}</div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-white/50 mt-0.5">{s.v}</div>
                  </div>
                ))}
              </div>

              <a
                href={INTAKE_URL}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="contact-cta-intake"
                className="inline-flex items-center justify-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink"
              >
                Start full intake <ArrowRight size={14} weight="bold" />
              </a>
              <a
                href={EVERFIT_URL}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="contact-cta-everfit"
                className="mt-3 inline-flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-white/60 hover:text-[#FF1B91] transition-colors"
              >
                Or skip ahead — join Everfit <ArrowUpRight size={12} weight="bold" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
